import React, { MutableRefObject, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { default as Form } from '../../components/connectedForm';
import OverviewContainer from '../../components/connectedForm/OverviewContainer';
import OverviewCard from '../../components/connectedForm/OverviewCard';
import OverviewCardTitle from '../../components/connectedForm/OverviewCardTitle';
import OverviewCardDivider from '../../components/connectedForm/OverviewCardDivider';
import OverviewCardSubTitle from '../../components/connectedForm/OverviewCardSubTitle';
import OverviewCardSubDivider from '../../components/connectedForm/OverviewCardSubDivider';
import OverviewLabel from '../../components/connectedForm/OverviewLabel';
import OverviewField from '../../components/connectedForm/OverviewField';
import OverviewInlineField from '../../components/connectedForm/OverviewInlineField';
import getIn from '../../components/connectedForm/utils/getIn';
import { formatDate } from '../../utils/DateHelper';
import { getPrimaryContact } from '../../utils/getPrimary';
import { getPrimaryDeliveryContact } from '../../utils/getPrimaryDelivery';
import { addressUtil } from '../../utils/AddressUtils';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import SalesInvoiceYearToDateCombinedQuery from '../../graphql/queries/salesLedger/SalesInvoiceYearToDateCombinedQuery.graphql';
import { mdiAlertCircleOutline } from '@mdi/js';
import theme from '../../styles/themes/default/defaultTheme';
import { useCompany } from '../../utils/FetchConfig';
import { useEventDetails } from '../diary/DiaryHooks';
import SalesInvoiceAnalysisCard from '../salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysisCard';
import { EmbeddedSalesInvoiceOptions } from '../salesLedger/salesInvoiceAnalysis/SalesInvoiceAnalysis';
import {
  AnalysisDimension,
  AnalysisMeasure,
  FilterOperation,
  ICustomer,
  IDimensionFilterInput,
  IFilterInput,
  IOverdueBalance,
  ISalesInvoiceYearToDateCombinedQueryResult,
  ISalesInvoiceYearToDateCombinedQueryVariables,
} from '../../graphql/schema/p4types';
import { checkPermission } from '../../utils/OperationPermissions';
import AppContext from '../../components/Context/AppContext';
import CurrencyContext from '../../components/Context/CurrencyContext';
import { useCurrentFinancialYear } from '../../utils/company/useFinancialCalendars';

export type ICustomerOverviewTabProps = {
  id: number;
  values: ICustomer;
  sidebar?: boolean;
  tabPanelRef?: MutableRefObject<any>;
  overdueBalance: IOverdueBalance;
};
const CustomerOverviewTab = ({ id, values, sidebar, tabPanelRef, overdueBalance }: ICustomerOverviewTabProps) => {
  const company = useCompany();
  const app = useContext(AppContext);

  const salesAnalysisPermitted = checkPermission({ app }, `SALES_LEDGER_ANALYTICS`);
  const customerContactsPermitted = checkPermission({ app }, `CUSTOMER_CONTACT_VIEW`);
  const tasksPermitted = checkPermission({ app }, `TASK_VIEW`);
  const creditControlPermitted = checkPermission({ app }, `CUSTOMER_CREDIT_CONTROL_VIEW`);
  const transactionsPermitted = checkPermission({ app }, `SALES_LEDGER_TRANSACTIONS_VIEW`);

  const eventPredefinedFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'customer.id',
        value: `${id}`,
      },
    },
  ];

  const predefinedAnalysisFilters: Array<IDimensionFilterInput> = [
    {
      dimension: AnalysisDimension.Customer,
      id: id,
    },
  ];

  const current = useCurrentFinancialYear();
  const currentCalendar = current ?? company?.currentFinancialYear;

  const variables = normalizeVariables<ISalesInvoiceYearToDateCombinedQueryVariables>({
    measure: AnalysisMeasure.Sales,
    measure2: AnalysisMeasure.Profit,
    measure3: AnalysisMeasure.Unit,
    financialCalendar: currentCalendar,
    dimensionFilters: predefinedAnalysisFilters,
  });

  const { data: statsData } = useQuery<
    ISalesInvoiceYearToDateCombinedQueryResult,
    ISalesInvoiceYearToDateCombinedQueryVariables
  >(SalesInvoiceYearToDateCombinedQuery, {
    variables: variables,
    fetchPolicy: 'network-only',
  });

  const statSales = statsData?.data?.[0]?.value ?? 0;
  const statProfit = statsData?.data2?.[0]?.value ?? 0;
  const events = useEventDetails(eventPredefinedFilters);

  const primaryContact = getPrimaryContact(values);
  const primaryDelivery = getPrimaryDeliveryContact(values);

  const cols = sidebar ? 1 : 1 / 3;

  function formatCashProforma() {
    const defaultCashStatus = getIn(values, 'defaultCashStatus');
    return defaultCashStatus ? defaultCashStatus.replace(/_/g, ' ') : '';
  }
  const overViewTitle = `Total Sales Current Year (${currentCalendar?.year})`;

  return (
    <CurrencyContext.Provider value={{ currency: values.currency || company?.baseCurrency }}>
      <OverviewContainer sidebar={sidebar}>
        <Form.Row>
          <Form.Fieldset cols={cols}>
            <OverviewCard>
              <OverviewCardTitle
                title="Contact information"
                linkTabLabel="Details"
                linkTabId="main"
                modal={sidebar}
                type="customer"
                entityId={id}
                tabPanelRef={tabPanelRef}
              />
              <OverviewField value={values.reference} list={true} primary={true} type="text" />
              <OverviewField value={values.name} list={true} primary={true} type="text" />
              <OverviewField value={values.email || 'No email address'} list={true} type="text" />
              <OverviewField value={values.telephoneNumber || 'No telephone number'} list={true} type="text" />
              <OverviewField value={values.website || 'No website address'} list={true} type="text" />
              <OverviewCardDivider />
              <OverviewCardSubTitle title="Addresses" />
              <OverviewField
                label="Invoice"
                value={addressUtil(values.headOffice?.address ? values.headOffice.address : values.address)}
                type="text"
              />
              <OverviewField
                label="Delivery"
                value={addressUtil(primaryDelivery?.address ? primaryDelivery.address : values.address)}
                type="text"
              />
              {customerContactsPermitted ? (
                <>
                  <OverviewCardDivider />
                  <OverviewCardSubTitle
                    title="Primary Contact"
                    linkTabLabel="Contacts"
                    linkTabId="contacts"
                    tabPanelRef={tabPanelRef}
                  />
                  <OverviewField
                    value={primaryContact?.name?.fullName || 'No primary contact'}
                    list={true}
                    type="text"
                  />
                  <OverviewField value={primaryContact?.email} list={true} type="text" />
                  <OverviewField value={primaryContact?.telephoneNumber} list={true} type="text" />
                </>
              ) : null}
            </OverviewCard>
            {tasksPermitted ? (
              <OverviewCard>
                <OverviewCardTitle title="Tasks" linkTabLabel="Tasks" linkTabId="events" tabPanelRef={tabPanelRef} />
                <OverviewLabel label={`Showing ${events.count} of ${events.totalCount} tasks`} />
                <OverviewCardDivider />
                {events.list}
              </OverviewCard>
            ) : null}
          </Form.Fieldset>
          <Form.Fieldset cols={sidebar ? 1 : 2 / 3}>
            {salesAnalysisPermitted && !sidebar ? (
              <SalesInvoiceAnalysisCard
                predefinedFilters={predefinedAnalysisFilters}
                ready={!!id}
                embedded={EmbeddedSalesInvoiceOptions.Customer}
                defaultLabel={values.reference}
                graphRightClickEnabled={true}
                cardHeader={
                  <Form.Fieldset cols={1}>
                    <OverviewCardTitle
                      title={overViewTitle}
                      linkTabId="analysis"
                      linkTabLabel="Analytics"
                      tabPanelRef={tabPanelRef}
                      hint={'Right click on graph for additional options.'}
                    />
                    <Form.Row>
                      <Form.Fieldset cols={1 / 2}>
                        <OverviewInlineField
                          label="Sales"
                          value={statSales}
                          decimals={2}
                          style={{
                            borderRight: '#cacaca',
                            borderRightWidth: 2,
                            borderRightStyle: 'solid',
                          }}
                          type={'currency'}
                        />
                      </Form.Fieldset>
                      <Form.Fieldset cols={1 / 2}>
                        <OverviewInlineField label="Profit" value={statProfit} decimals={2} type={'currency'} />
                      </Form.Fieldset>
                    </Form.Row>
                  </Form.Fieldset>
                }
              />
            ) : null}
            {creditControlPermitted || transactionsPermitted ? (
              <OverviewCard>
                <Form.Row>
                  {creditControlPermitted ? (
                    <Form.Fieldset cols={1 / 2}>
                      <OverviewCardTitle
                        title="Credit Control"
                        linkTabLabel="Balances"
                        linkTabId="credit"
                        tabPanelRef={tabPanelRef}
                      />
                      <OverviewInlineField
                        label="Total balance"
                        value={getIn(values, 'balance.balance')}
                        decimals={2}
                        type={'currency'}
                      />
                      <OverviewCardSubDivider />
                      <OverviewInlineField
                        label="Outstanding orders"
                        value={values.outstandingOrderValue}
                        decimals={2}
                        type={'currency'}
                      />
                      <OverviewCardSubDivider />
                      <OverviewInlineField
                        label="Outstanding quotes"
                        value={values.outstandingQuoteValue}
                        decimals={2}
                        type={'currency'}
                      />
                      <OverviewCardSubDivider />
                      <OverviewInlineField
                        label="Credit limit"
                        value={getIn(values, 'creditLimit')}
                        decimals={2}
                        type={'currency'}
                      />
                      <OverviewCardSubDivider />
                      <OverviewInlineField
                        label="Overdue Invoices"
                        value={overdueBalance?.overdueInvoiceAmount}
                        decimals={2}
                        type={'currency'}
                      />
                      <OverviewCardSubDivider />
                      <OverviewInlineField
                        label="Overdue Balance"
                        value={overdueBalance?.overdueAmount ?? 0}
                        decimals={2}
                        icon={{
                          path: mdiAlertCircleOutline,
                          color: theme.colors.notificationInfo,
                        }}
                        hint={'Includes Credits, Receipts & Opening Balances'}
                        type={'currency'}
                      />
                      <OverviewCardSubDivider />
                      <OverviewInlineField
                        label="Currency"
                        value={getIn(values, 'currency.description') || 'GBP'}
                        type="text"
                        fontSize="13px"
                      />
                    </Form.Fieldset>
                  ) : null}
                  {transactionsPermitted ? (
                    <Form.Fieldset cols={1 / 2}>
                      <OverviewCardSubTitle
                        title="Status"
                        linkTabLabel="Transactions"
                        linkTabId="transactions"
                        tabPanelRef={tabPanelRef}
                      />
                      <OverviewInlineField
                        label="Last transaction"
                        value={formatDate(getIn(values, 'lastTransactionDate'))}
                        type="text"
                        fontSize="13px"
                      />
                      <OverviewInlineField
                        label="Cash/Pro forma"
                        value={formatCashProforma()}
                        type="text"
                        fontSize="13px"
                      />
                      <OverviewInlineField
                        label="Payment terms"
                        value={getIn(values, 'paymentTerm.description')}
                        type="text"
                        fontSize="13px"
                      />
                      <OverviewInlineField
                        label="Account status"
                        value={getIn(values, 'customerAccountStatus.description')}
                        type="text"
                        fontSize="13px"
                      />
                      <OverviewInlineField
                        label="VAT Status"
                        value={getIn(values, 'vatBusinessCategory.description')}
                        type="text"
                        fontSize="13px"
                      />
                      <OverviewInlineField
                        label="Nominal category"
                        value={getIn(values, 'nominalBusinessCategory.description')}
                        type="text"
                        fontSize="13px"
                      />
                    </Form.Fieldset>
                  ) : null}
                </Form.Row>
              </OverviewCard>
            ) : null}
          </Form.Fieldset>
        </Form.Row>
      </OverviewContainer>
    </CurrencyContext.Provider>
  );
};

export default CustomerOverviewTab;
